import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import DateTimeHeader from "../components/DateTimeHeader";
import {
  Bell,
  Moon,
  Sun,
  User,
  Settings as SettingsIcon,
  Lock,
  Globe,
  Camera,
  Mic,
  Video,
  Speaker,
  Volume1,
  Shield,
  Calendar as CalendarIcon,
  Mail,
  Smartphone,
  Monitor,
  Volume2,
  ShieldCheck,
  ChevronRight,
  RefreshCw,
  Plus,
  Check,
  Eye,
  EyeOff,
  Key,
  Headphones,
  VolumeX,
  X,
} from "lucide-react";
import ProfileModal from "../components/ProfileModal";
import ToggleSwitch from "../components/ToggleSwitch";
import Dropdown from "../components/Dropdown";
import { useAuth } from "../contexts/AuthContext";
import { useMeeting } from "../contexts/MeetingContext";
import NavBar from "../components/NavBar";
import { useTheme } from "../contexts/ThemeContext";
import { usePreference } from "../contexts/PreferenceContext";
import { generateToken, flushLocalMessages } from "../utils/firebase";

interface UserProfile {
  id: number;
  first_name: string;
  last_name?: string;
  email: string;
  bio?: string | null;
  job_title?: string | null;
  department?: string | null;
  time_zone?: string;
  date_format?: string;
  isTwoFactorEnabled?: boolean;
  twoFactorSecret?: string | null;
  profile_picture?: string | null;
  created_at?: string;
  skills?: string[] | null;
  language?: string;
  meeting_reminder_enabled?: boolean;
  meeting_reminder_time?: string;
  meeting_reminder_before_start?: boolean;
}

interface ProfileResponse {
  user: UserProfile;
}

const Settings: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  // const [darkMode, setDarkMode] = useState(false);
  const [time, setTime] = useState<string>("");

  // Toggle states
  const [desktopNotifications, setDesktopNotifications] = useState(false);
  const [autoMicMuted, setAutoMicMuted] = useState(false);
  const [autoCameraOff, setAutoCameraOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<
    "profile" | "security" | "preferences" | "integrations" | "devices"
  >("profile");

  // Add these states for calendar integration
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isConnectingOutlook, setIsConnectingOutlook] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [autoSyncMeetings, setAutoSyncMeetings] = useState(true);
  const [syncMeetingUpdates, setSyncMeetingUpdates] = useState(true);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  // Get the calendar connection methods from AuthContext
  const {
    connectGoogleCalendar,
    connectOutlookCalendar,
    disconnectGoogleCalendar,
    disconnectOutlookCalendar,
    refreshUser,
    // user
  } = useAuth();

  // Handle Google Calendar connection
  const handleConnectGoogle = async () => {
    try {
      setIsConnectingGoogle(true);
      setCalendarError(null);
      const authUrl = await connectGoogleCalendar();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      setCalendarError('Failed to connect to Google Calendar');
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  // Handle Outlook Calendar connection
  const handleConnectOutlook = async () => {
    try {
      setIsConnectingOutlook(true);
      setCalendarError(null);
      const authUrl = await connectOutlookCalendar();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error connecting to Outlook Calendar:', error);
      setCalendarError('Failed to connect to Outlook Calendar');
    } finally {
      setIsConnectingOutlook(false);
    }
  };

  // Handle Google Calendar disconnection
  const handleDisconnectGoogle = async () => {
    try {
      setIsConnectingGoogle(true);
      setCalendarError(null);
      await disconnectGoogleCalendar();
      // Success message or UI update
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      setCalendarError('Failed to disconnect Google Calendar');
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  // Handle Outlook Calendar disconnection
  const handleDisconnectOutlook = async () => {
    try {
      setIsConnectingOutlook(true);
      setCalendarError(null);
      await disconnectOutlookCalendar();
      // Success message or UI update
    } catch (error) {
      console.error('Error disconnecting Outlook Calendar:', error);
      setCalendarError('Failed to disconnect Outlook Calendar');
    } finally {
      setIsConnectingOutlook(false);
    }
  };

  // Check for calendar connection status from URL params (after OAuth redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const calendarType = urlParams.get('calendar');
    const status = urlParams.get('status');

    if (calendarType && status) {
      if (status === 'success') {
        setSaveMessage(`${calendarType.charAt(0).toUpperCase() + calendarType.slice(1)} Calendar connected successfully`);
        // Refresh user from backend so outlook_calendar_enabled updates in UI
        refreshUser();
      } else {
        setCalendarError(`Failed to connect to ${calendarType.charAt(0).toUpperCase() + calendarType.slice(1)} Calendar`);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  const { darkMode, toggleDarkMode } = useTheme();


  const timeZones = [
    { value: "automatic", label: "Automatic" },
    { value: "Etc/GMT+12", label: "(UTC-12:00) International Date Line West" },
    { value: "Etc/GMT+11", label: "(UTC-11:00) Coordinated Universal Time-11" },
    { value: "America/Adak", label: "(UTC-10:00) Aleutian Islands" },
    { value: "Pacific/Honolulu", label: "(UTC-10:00) Hawaii" },
    { value: "Pacific/Marquesas", label: "(UTC-09:30) Marquesas Islands" },
    { value: "America/Anchorage", label: "(UTC-09:00) Alaska" },
    { value: "Etc/GMT+9", label: "(UTC-09:00) Coordinated Universal Time-09" },
    { value: "America/Tijuana", label: "(UTC-08:00) Baja California" },
    { value: "Etc/GMT+8", label: "(UTC-08:00) Coordinated Universal Time-08" },
    { value: "America/Los_Angeles", label: "(UTC-08:00) Pacific Time (US & Canada)" },
    { value: "America/Phoenix", label: "(UTC-07:00) Arizona" },
    { value: "America/Mazatlan", label: "(UTC-07:00) La Paz, Mazatlan" },
    { value: "America/Denver", label: "(UTC-07:00) Mountain Time (US & Canada)" },
    { value: "America/Whitehorse", label: "(UTC-07:00) Yukon" },
    { value: "America/Guatemala", label: "(UTC-06:00) Central America" },
    { value: "America/Chicago", label: "(UTC-06:00) Central Time (US & Canada)" },
    { value: "Pacific/Easter", label: "(UTC-06:00) Easter Island" },
    { value: "America/Mexico_City", label: "(UTC-06:00) Guadalajara, Mexico City, Monterrey" },
    { value: "America/Regina", label: "(UTC-06:00) Saskatchewan" },
    { value: "America/Bogota", label: "(UTC-05:00) Bogota, Lima, Quito, Rio Branco" },
    { value: "America/Cancun", label: "(UTC-05:00) Chetumal" },
    { value: "America/New_York", label: "(UTC-05:00) Eastern Time (US & Canada)" },
    { value: "America/Port-au-Prince", label: "(UTC-05:00) Haiti" },
    { value: "America/Havana", label: "(UTC-05:00) Havana" },
    { value: "America/Indianapolis", label: "(UTC-05:00) Indiana (East)" },
    { value: "America/Grand_Turk", label: "(UTC-05:00) Turks and Caicos" },
    { value: "America/Asuncion", label: "(UTC-04:00) Asuncion" },
    { value: "America/Halifax", label: "(UTC-04:00) Atlantic Time (Canada)" },
    { value: "America/Caracas", label: "(UTC-04:00) Caracas" },
    { value: "America/Cuiaba", label: "(UTC-04:00) Cuiaba" },
    { value: "America/La_Paz", label: "(UTC-04:00) Georgetown, La Paz, Manaus, San Juan" },
    { value: "America/Santiago", label: "(UTC-04:00) Santiago" },
    { value: "America/St_Johns", label: "(UTC-03:30) Newfoundland" },
    { value: "America/Araguaina", label: "(UTC-03:00) Araguaina" },
    { value: "America/Sao_Paulo", label: "(UTC-03:00) Brasilia" },
    { value: "America/Cayenne", label: "(UTC-03:00) Cayenne, Fortaleza" },
    { value: "America/Buenos_Aires", label: "(UTC-03:00) City of Buenos Aires" },
    { value: "America/Montevideo", label: "(UTC-03:00) Montevideo" },
    { value: "America/Punta_Arenas", label: "(UTC-03:00) Punta Arenas" },
    { value: "America/Miquelon", label: "(UTC-03:00) Saint Pierre and Miquelon" },
    { value: "America/Bahia", label: "(UTC-03:00) Salvador" },
    { value: "Etc/GMT+2", label: "(UTC-02:00) Coordinated Universal Time-02" },
    { value: "America/Nuuk", label: "(UTC-02:00) Greenland" },
    { value: "Atlantic/South_Georgia", label: "(UTC-02:00) Mid-Atlantic - Old" },
    { value: "Atlantic/Azores", label: "(UTC-01:00) Azores" },
    { value: "Atlantic/Cape_Verde", label: "(UTC-01:00) Cabo Verde Is." },
    { value: "Africa/Casablanca", label: "(UTC) Casablanca" },
    { value: "Etc/UTC", label: "(UTC) Coordinated Universal Time" },
    { value: "Europe/London", label: "(UTC) Dublin, Edinburgh, Lisbon, London" },
    { value: "Atlantic/Reykjavik", label: "(UTC) Monrovia, Reykjavik" },
    { value: "Europe/Berlin", label: "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna" },
    { value: "Europe/Belgrade", label: "(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague" },
    { value: "Europe/Brussels", label: "(UTC+01:00) Brussels, Copenhagen, Madrid, Paris" },
    { value: "Europe/Sarajevo", label: "(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb" },
    { value: "Africa/Lagos", label: "(UTC+01:00) West Central Africa" },
    { value: "Europe/Athens", label: "(UTC+02:00) Athens, Bucharest" },
    { value: "Asia/Beirut", label: "(UTC+02:00) Beirut" },
    { value: "Africa/Cairo", label: "(UTC+02:00) Cairo" },
    { value: "Europe/Chisinau", label: "(UTC+02:00) Chisinau" },
    { value: "Asia/Gaza", label: "(UTC+02:00) Gaza, Hebron" },
    { value: "Africa/Johannesburg", label: "(UTC+02:00) Harare, Pretoria" },
    { value: "Europe/Helsinki", label: "(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius" },
    { value: "Asia/Jerusalem", label: "(UTC+02:00) Jerusalem" },
    { value: "Africa/Juba", label: "(UTC+02:00) Juba" },
    { value: "Europe/Kaliningrad", label: "(UTC+02:00) Kaliningrad" },
    { value: "Africa/Khartoum", label: "(UTC+02:00) Khartoum" },
    { value: "Africa/Tripoli", label: "(UTC+02:00) Tripoli" },
    { value: "Africa/Windhoek", label: "(UTC+02:00) Windhoek" },
    { value: "Asia/Amman", label: "(UTC+03:00) Amman" },
    { value: "Asia/Baghdad", label: "(UTC+03:00) Baghdad" },
    { value: "Asia/Damascus", label: "(UTC+03:00) Damascus" },
    { value: "Europe/Istanbul", label: "(UTC+03:00) Istanbul" },
    { value: "Asia/Riyadh", label: "(UTC+03:00) Kuwait, Riyadh" },
    { value: "Europe/Minsk", label: "(UTC+03:00) Minsk" },
    { value: "Europe/Moscow", label: "(UTC+03:00) Moscow, St. Petersburg" },
    { value: "Africa/Nairobi", label: "(UTC+03:00) Nairobi" },
    { value: "Europe/Volgograd", label: "(UTC+03:00) Volgograd" },
    { value: "Asia/Tehran", label: "(UTC+03:30) Tehran" },
    { value: "Asia/Dubai", label: "(UTC+04:00) Abu Dhabi, Muscat" },
    { value: "Europe/Astrakhan", label: "(UTC+04:00) Astrakhan, Ulyanovsk" },
    { value: "Asia/Baku", label: "(UTC+04:00) Baku" },
    { value: "Europe/Samara", label: "(UTC+04:00) Izhevsk, Samara" },
    { value: "Indian/Mauritius", label: "(UTC+04:00) Port Louis" },
    { value: "Europe/Saratov", label: "(UTC+04:00) Saratov" },
    { value: "Asia/Tbilisi", label: "(UTC+04:00) Tbilisi" },
    { value: "Asia/Yerevan", label: "(UTC+04:00) Yerevan" },
    { value: "Asia/Kabul", label: "(UTC+04:30) Kabul" },
    { value: "Asia/Ashgabat", label: "(UTC+05:00) Ashgabat, Toshkent" },
    { value: "Asia/Almaty", label: "(UTC+05:00) Astana" },
    { value: "Asia/Yekaterinburg", label: "(UTC+05:00) Ekaterinburg" },
    { value: "Asia/Karachi", label: "(UTC+05:00) Islamabad, Karachi" },
    { value: "Asia/Kolkata", label: "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi" },
    { value: "Asia/Colombo", label: "(UTC+05:30) Sri Jayawardenepura" },
    { value: "Asia/Kathmandu", label: "(UTC+05:45) Kathmandu" },
    { value: "Asia/Bishkek", label: "(UTC+06:00) Bishkek" },
    { value: "Asia/Dhaka", label: "(UTC+06:00) Dhaka" },
    { value: "Asia/Omsk", label: "(UTC+06:00) Omsk" },
    { value: "Asia/Yangon", label: "(UTC+06:30) Yangon (Rangoon)" },
    { value: "Asia/Bangkok", label: "(UTC+07:00) Bangkok, Hanoi, Jakarta" },
    { value: "Asia/Barnaul", label: "(UTC+07:00) Barnaul, Gorno-Altaysk" },
    { value: "Asia/Hovd", label: "(UTC+07:00) Hovd" },
    { value: "Asia/Krasnoyarsk", label: "(UTC+07:00) Krasnoyarsk" },
    { value: "Asia/Novosibirsk", label: "(UTC+07:00) Novosibirsk" },
    { value: "Asia/Tomsk", label: "(UTC+07:00) Tomsk" },
    { value: "Asia/Shanghai", label: "(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi" },
    { value: "Asia/Irkutsk", label: "(UTC+08:00) Irkutsk" },
    { value: "Asia/Singapore", label: "(UTC+08:00) Kuala Lumpur, Singapore" },
    { value: "Australia/Perth", label: "(UTC+08:00) Perth" },
    { value: "Asia/Taipei", label: "(UTC+08:00) Taipei" },
    { value: "Asia/Ulaanbaatar", label: "(UTC+08:00) Ulaanbaatar" },
    { value: "Australia/Eucla", label: "(UTC+08:45) Eucla" },
    { value: "Asia/Chita", label: "(UTC+09:00) Chita" },
    { value: "Asia/Tokyo", label: "(UTC+09:00) Osaka, Sapporo, Tokyo" },
    { value: "Asia/Pyongyang", label: "(UTC+09:00) Pyongyang" },
    { value: "Asia/Seoul", label: "(UTC+09:00) Seoul" },
    { value: "Asia/Yakutsk", label: "(UTC+09:00) Yakutsk" },
    { value: "Australia/Adelaide", label: "(UTC+09:30) Adelaide" },
    { value: "Australia/Darwin", label: "(UTC+09:30) Darwin" },
    { value: "Australia/Brisbane", label: "(UTC+10:00) Brisbane" },
    { value: "Australia/Sydney", label: "(UTC+10:00) Canberra, Melbourne, Sydney" },
    { value: "Pacific/Guam", label: "(UTC+10:00) Guam, Port Moresby" },
    { value: "Australia/Hobart", label: "(UTC+10:00) Hobart" },
    { value: "Asia/Vladivostok", label: "(UTC+10:00) Vladivostok" },
    { value: "Australia/Lord_Howe", label: "(UTC+10:30) Lord Howe Island" },
    { value: "Pacific/Bougainville", label: "(UTC+11:00) Bougainville Island" },
    { value: "Asia/Chokurdakh", label: "(UTC+11:00) Chokurdakh" },
    { value: "Asia/Magadan", label: "(UTC+11:00) Magadan" },
    { value: "Pacific/Norfolk", label: "(UTC+11:00) Norfolk Island" },
    { value: "Asia/Sakhalin", label: "(UTC+11:00) Sakhalin" },
    { value: "Pacific/Guadalcanal", label: "(UTC+11:00) Solomon Is., New Caledonia" },
    { value: "Asia/Anadyr", label: "(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky" },
    { value: "Pacific/Auckland", label: "(UTC+12:00) Auckland, Wellington" },
    { value: "Etc/GMT-12", label: "(UTC+12:00) Coordinated Universal Time+12" },
    { value: "Pacific/Fiji", label: "(UTC+12:00) Fiji" },
    { value: "Asia/Kamchatka", label: "(UTC+12:00) Petropavlovsk-Kamchatsky - Old" },
    { value: "Pacific/Chatham", label: "(UTC+12:45) Chatham Islands" },
    { value: "Etc/GMT-13", label: "(UTC+13:00) Coordinated Universal Time+13" },
    { value: "Pacific/Tongatapu", label: "(UTC+13:00) Nuku'alofa" },
    { value: "Pacific/Apia", label: "(UTC+13:00) Samoa" },
    { value: "Pacific/Kiritimati", label: "(UTC+14:00) Kiritimati Island" }
  ];


  // Profile states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [bio, setBio] = useState(
    ""
  );
  const [skills, setSkills] = useState([
    ""
  ]);
  const [newSkill, setNewSkill] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [twoFactorQRCode, setTwoFactorQRCode] = useState<string>('');
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] = useState('');
  const [twoFactorSetupStep, setTwoFactorSetupStep] = useState<'initial' | 'verify'>('initial');
  const { setupTwoFactor, verifyAndEnableTwoFactor, user, disableTwoFactor, myProfile, updateProfile, uploadProfileImage, changePassword } = useAuth();


  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disableToken, setDisableToken] = useState('');

  // Security states
  const [twoFactor, setTwoFactor] = useState(false);
  const [passwordLastChanged] = useState("March 10, 2024");

  // Preferences states
  const [language, setLanguage] = useState("");
  const [timeZone, setTimeZone] = useState("automatic");
  console.log("time zone check", timeZone);

  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [meetingReminders, setMeetingReminders] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState("00:10:00");
  const [meetingStartNotifications, setMeetingStartNotifications] =
    useState(true);
  // Device states
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMicrophone, setSelectedMicrophone] = useState(
    ""
  );
  const [selectedSpeaker, setSelectedSpeaker] = useState("");
  const [mirrorVideo, setMirrorVideo] = useState(true);
  const [micVolume, setMicVolume] = useState(75);
  const [speakerVolume, setSpeakerVolume] = useState(80);
  const [micInputLevel, setMicInputLevel] = useState(0);
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  type DeviceSummary = { id: string; label: string; kind: MediaDeviceKind };
  type DeviceInfo = { cameras: DeviceSummary[]; microphones: DeviceSummary[]; speakers: DeviceSummary[] };
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({ cameras: [], microphones: [], speakers: [] });
  const [deviceLoading, setDeviceLoading] = useState<boolean>(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [speakerTesting, setSpeakerTesting] = useState(false);
  const [audioCtx, setAudioCtx] = useState<AudioContext | null>(null);
  const [micTestRunning, setMicTestRunning] = useState(false);
  const [cameraTest, setCameraTest] = useState(false);
  const [micStreamHandle, setMicStreamHandle] = useState<MediaStream | null>(null);
  const [micAnalyser, setMicAnalyser] = useState<AnalyserNode | null>(null);
  const micLevelTimer = useRef<number | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [micAudioCtx, setMicAudioCtx] = useState<AudioContext | null>(null);
  // const [myProfileData, setMyProfileData] = useState(null);
  const [myProfileData, setMyProfileData] = useState<ProfileResponse | null>(null);
  // console.log("myProfileData",myProfileData?.user?.job_title);
  // console.log("helllllo",myProfileData);





  // Notification dropdown state
  const [showNotifications, setShowNotifications] = useState(false);
  const notifications = [
    { id: 1, text: "Your meeting starts in 10 minutes.", unread: true },
    { id: 2, text: "Profile updated successfully.", unread: false },
    { id: 3, text: "New message from John Doe.", unread: true },
  ];
  const bellRef = useRef<HTMLButtonElement>(null);

  // Save functionality
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const { setDateFormatPreference, savePreferences } = usePreference();
  // const { savePreferences } = usePreference();

  const [preview, setPreview] = useState<string>(
    "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);



  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    setSelectedImageFile(file);
    markAsChanged();
  };

  // open file picker on camera button click
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  // remove selected image
  const handleRemovePhoto = () => {
    setPreview("https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png");
  };

  // Time update effect
  React.useEffect(() => {
    const update = () =>
      setTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setFirstName(user?.first_name || '');
    setLastName(user?.last_name || '');
    setTwoFactor(user?.isTwoFactorEnabled || false);
  }, [user])

  useEffect(() => {
    setJobTitle(myProfileData?.user?.job_title || '');
    setDepartment(myProfileData?.user?.department || '');
    setSkills(myProfileData?.user?.skills || []);
    setBio(myProfileData?.user?.bio || '');
    setLanguage(myProfileData?.user?.language || '');
    setTimeZone(myProfileData?.user?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    setDateFormat(myProfileData?.user?.date_format || 'MM/DD/YYYY');
    // try {
    //   setTimezone(myProfileData?.user?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    //   setDateFormatPreference(myProfileData?.user?.date_format || 'MM/DD/YYYY');
    // } catch {}
    setMeetingReminders(myProfileData?.user?.meeting_reminder_enabled || false);
    setReminderMinutes(myProfileData?.user?.meeting_reminder_time || "00:10:00");
    setMeetingStartNotifications(myProfileData?.user?.meeting_reminder_before_start || false);
    { myProfileData?.user?.profile_picture != null && setPreview(`${process.env.REACT_APP_BACKEND_URL}${myProfileData?.user?.profile_picture}`); }
  }, [myProfileData])

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("New passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      alert("Password must be at least 8 characters long");
      return;
    }
    try {
      await changePassword(currentPassword, newPassword);
      setSaveMessage('Password updated successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const updateUserProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    setError('');
    try {
      if (selectedImageFile) {
        const formData = new FormData();
        formData.append("profile_picture", selectedImageFile);
        formData.append("userId", user?.id?.toString() || "");
        const result = await uploadProfileImage(formData);
        setPreview(`${process.env.REACT_APP_BACKEND_URL}${result.imageUrl}`);
        setSelectedImageFile(null);
      }
      await updateProfile({
        job_title: jobTitle,
        department: department,
        bio: bio,
        skills: skills,
        language: language,
        time_zone: timeZone,
        date_format: dateFormat,
        meeting_reminder_enabled: meetingReminders,
        meeting_reminder_time: reminderMinutes,
        meeting_reminder_before_start: meetingStartNotifications,
      });
      try {

        await savePreferences(timeZone, dateFormat);
      } catch { }
      setHasUnsavedChanges(false);
      setSaveMessage('Profile updated successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message);
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };


  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        showNotifications &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setShowNotifications(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showNotifications]);

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await disableTwoFactor(disableToken);
      setShowDisable2FAModal(false);
      // Show success message
      setSaveMessage('Two-factor authentication disabled successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error.message);
      setError('Invalid OTP code. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Add this to your JSX where you show the 2FA toggle
  const handleTwoFactorToggle = async () => {
    if (twoFactor) {
      // If 2FA is enabled, show disable modal
      setShowDisable2FAModal(true);
      setError('');
    } else {
      // Your existing code for enabling 2FA
      try {
        const { qrCodeDataURL } = await setupTwoFactor();
        setTwoFactorQRCode(qrCodeDataURL);
        setShowTwoFactorModal(true);
      } catch (error) {
        console.error('Failed to setup 2FA:', error);
      }
    }
  };



  const [notificationPermission, setNotificationPermission] = useState<string>(() => {
    try {
      if (typeof window === "undefined") return "unsupported";

      const Notif =
        "Notification" in window ? window.Notification : null;

      if (!Notif) return "unsupported";

      return (
        localStorage.getItem("notification_permission") ||
        Notif.permission ||
        "default"
      );
    } catch {
      return "default";
    }
  });


  const handleVerifyTwoFactor = async () => {
    try {
      await verifyAndEnableTwoFactor(twoFactorVerificationCode);
      setTwoFactor(true);
      setShowTwoFactorModal(false);
      // Show success message
      setSaveMessage('Two-factor authentication enabled successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to verify 2FA:', error);
      setError('Invalid OTP code. Please try again.');
      // Show error message
      setSaveMessage('Failed to verify 2FA code');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleMyProfile = async () => {
    try {
      const profile = await myProfile();
      // setUser(profile);
      console.log("profile data", profile);
      setMyProfileData({ user: profile });

    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  useEffect(() => {
    handleMyProfile();
  }, [user])

  const tabs = [
    {
      id: "profile",
      label: "Profile",
      icon: User,
      description: "Personal information",
    },
    {
      id: "security",
      label: "Security",
      icon: Lock,
      description: "Account security",
    },
    {
      id: "preferences",
      label: "Preferences",
      icon: Globe,
      description: "App settings",
    },
    {
      id: "integrations",
      label: "Integrations",
      icon: CalendarIcon,
      description: "Calendar & apps",
    },
    {
      id: "devices",
      label: "Devices",
      icon: Monitor,
      description: "Audio & video",
    },
  ];

  const handleTestSpeaker = async () => {
    try {
      setSpeakerTesting(true);

      // 🔊 Create audio element
      const audio = new Audio();
      audio.src = "/test-tone.mp3"; // Path to test sound file
      audio.volume = speakerVolume / 100;

      //  Selected speaker device
      const selected = deviceInfo.speakers.find(
        s => s.label === selectedSpeaker
      );

      //  Set output device (Chrome / Edge)
      if ((audio as any).setSinkId && selected) {
        await (audio as any).setSinkId(selected.id);
      }

      await audio.play();

      audio.onended = () => {
        setSpeakerTesting(false);
      };

    } catch (err) {
      console.error("Speaker test failed:", err);
      setSpeakerTesting(false);
    }
  };



  async function openCameraTest() {
    setCameraError(null);
    setCameraLoading(true);
    setCameraTest(true);
    setCameraModalOpen(true); // Add this line - open modal first

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      setCameraLoading(false);

      // Set video source after modal is open
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play().catch(err => {
              console.error('Error playing video:', err);
              setCameraError('Failed to play camera feed');
            });
          };
        }
      }, 100);
    } catch (err: any) {
      setCameraError(err.name === 'NotAllowedError'
        ? 'Camera access was denied. Please allow camera access in your browser settings.'
        : 'Camera access denied or unavailable');
      setCameraLoading(false);
      setCameraModalOpen(false); // Close modal on error
    }
  }
  function closeCameraTest() {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (e) {
          console.error('Error stopping track:', e);
        }
      });
    }
    setCameraStream(null);
    setCameraModalOpen(false);
    setCameraTest(false);
    setCameraError(null);
    setCameraLoading(false);
  }

  async function retryCameraTest() {
    closeCameraTest();
    // Wait for state to update before opening again
    setTimeout(() => openCameraTest(), 100);
  }
  // async function retryCameraTest() {
  //   closeCameraTest();
  //   await openCameraTest();
  // }
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && cameraModalOpen) closeCameraTest();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cameraModalOpen]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => {
          try { track.stop(); } catch (e) { }
        });
      }
      stopMicTest();
    };
  }, []);

  async function startMicTest() {
    if (micTestRunning) return;
    try {
      setMicError(null);

      if (!navigator.mediaDevices?.getUserMedia) {
        setMicError("Microphone API not supported");
        return;
      }

      // 🎯 Selected microphone
      const selected = deviceInfo.microphones.find(
        m => m.label === selectedMicrophone
      );

      const constraints: MediaStreamConstraints = selected
        ? { audio: { deviceId: { exact: selected.id } }, video: false }
        : { audio: true, video: false };

      // 🎤 Get mic stream
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      const AC =
        window.AudioContext || (window as any).webkitAudioContext;

      if (!AC) {
        setMicError("AudioContext not supported");
        return;
      }

      //  CREATE + RESUME AUDIO CONTEXT (MOST IMPORTANT FIX)
      const ctx = new AC();
      await ctx.resume(); //  1st click issue FIXED

      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;

      source.connect(analyser);

      setMicAnalyser(analyser);
      setMicStreamHandle(stream);
      setMicAudioCtx(ctx);
      setMicTestRunning(true);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        try {
          analyser.getByteTimeDomainData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const v = (dataArray[i] - 128) / 128;
            sum += v * v;
          }

          const rms = Math.sqrt(sum / dataArray.length);
          const level = Math.min(100, Math.round(rms * 100));

          // 🔊 ONLY INPUT LEVEL (slider se independent)
          setMicInputLevel(level);

        } catch { }
      };

      micLevelTimer.current = window.setInterval(tick, 100);

    } catch (err: any) {
      const name = err?.name || "";

      if (name === "NotAllowedError")
        setMicError("Microphone permission denied");
      else if (name === "NotFoundError")
        setMicError("No microphone found");
      else if (name === "NotReadableError")
        setMicError("Microphone is in use by another application");
      else
        setMicError("Microphone access failed");
    }
  }

  function stopMicTest() {
    setMicTestRunning(false);

    if (micLevelTimer.current) {
      clearInterval(micLevelTimer.current);
      micLevelTimer.current = null;
    }

    if (micStreamHandle) {
      micStreamHandle.getTracks().forEach(t => t.stop());
      setMicStreamHandle(null);
    }

    if (micAnalyser) {
      micAnalyser.disconnect();
      setMicAnalyser(null);
    }

    if (micAudioCtx) {
      micAudioCtx.close();
      setMicAudioCtx(null);
    }

    setMicInputLevel(0);
  }

  useEffect(() => {
    return () => {
      stopMicTest();
      closeCameraTest();
      if (audioCtx) { try { audioCtx.close(); } catch { } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills([...skills, newSkill.trim()]);
      setNewSkill("");
      markAsChanged();
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter((skill) => skill !== skillToRemove));
    markAsChanged();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSkill();
    }
  };

  function cacheDeviceInfo(info: DeviceInfo) {
    try {
      localStorage.setItem('device_info', JSON.stringify({ info, ts: Date.now() }));
    } catch { }
  }
  function loadCachedDeviceInfo(): DeviceInfo | null {
    try {
      const raw = localStorage.getItem('device_info');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.info) return null;
      return parsed.info as DeviceInfo;
    } catch {
      return null;
    }
  }
  async function ensureLabels() {
    try {
      // Request only audio to avoid activating camera implicitly
      await navigator.mediaDevices.getUserMedia({ audio: false, video: false }).catch(() => { });
    } catch { }
  }
  async function fetchDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      setDeviceError('Media devices API not supported');
      return;
    }
    setDeviceLoading(true);
    setDeviceError(null);
    try {
      await ensureLabels();
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams: DeviceSummary[] = list.filter(d => d.kind === 'videoinput').map(d => ({ id: d.deviceId, label: d.label || 'Camera', kind: d.kind }));
      const mics: DeviceSummary[] = list.filter(d => d.kind === 'audioinput').map(d => ({ id: d.deviceId, label: d.label || 'Microphone', kind: d.kind }));
      const outs: DeviceSummary[] = list.filter(d => d.kind === 'audiooutput').map(d => ({ id: d.deviceId, label: d.label || 'Speaker', kind: d.kind }));
      const info: DeviceInfo = { cameras: cams, microphones: mics, speakers: outs };
      setDeviceInfo(info);
      cacheDeviceInfo(info);
      if (cams.length && !cams.some(c => c.label === selectedCamera)) setSelectedCamera(cams[0].label);
      if (mics.length && !mics.some(m => m.label === selectedMicrophone)) setSelectedMicrophone(mics[0].label);
      if (outs.length && !outs.some(s => s.label === selectedSpeaker)) setSelectedSpeaker(outs[0].label);
    } catch (err: any) {
      setDeviceError('Failed to fetch devices');
    } finally {
      setDeviceLoading(false);
    }
  }
  useEffect(() => {
    const cached = loadCachedDeviceInfo();
    if (cached) setDeviceInfo(cached);
    fetchDevices();
    const handler = () => fetchDevices();
    try {
      navigator.mediaDevices.addEventListener('devicechange', handler);
    } catch { }
    return () => {
      try { navigator.mediaDevices.removeEventListener('devicechange', handler); } catch { }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) { closeCameraTest(); stopMicTest(); }
    };
    const onPageHide = () => { closeCameraTest(); stopMicTest(); };
    const onBeforeUnload = () => { closeCameraTest(); stopMicTest(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  const handleClosePasswordModal = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordModal(false);
  };


  // Helper function to mark changes
  const markAsChanged = () => {
    setHasUnsavedChanges(true);
  };

  return (
    <div
      className={`min-h-screen ${darkMode
        ? "bg-gradient-to-br from-[#181824] to-[#232336]"
        : "bg-gradient-to-br from-gray-50 to-blue-50"
        }`}
    >
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main
        className={`flex-1 px-6 py-8 md:px-12 md:py-10 transition-all duration-200 ${collapsed ? "ml-20" : "ml-20 md:ml-52"
          }`}
      >
        {/* Top Bar */}
        <NavBar />

        {/* Page Title */}
        <div className="mb-8 mt-12">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
            <div>
              <h2
                className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Settings
              </h2>
            </div>
          </div>

        </div>

        {/* Settings Layout */}
        <div className="flex flex-col xl:flex-row gap-4 lg:gap-8">
          {/* Vertical Navigation */}
          <div className="w-full xl:w-80 flex-shrink-0">
            <div
              className={`rounded-2xl shadow-2xl border p-4 ${darkMode
                ? "bg-[#2c2c3a] border-white/10"
                : "bg-white border-gray-200"
                }`}
            >
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() =>
                        setActiveTab(
                          tab.id as
                          | "profile"
                          | "security"
                          | "preferences"
                          | "integrations"
                          | "devices"
                        )
                      }
                      className={`w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200 group ${activeTab === tab.id
                        ? darkMode
                          ? "bg-gradient-to-r from-pink-600/20 to-red-500/20 border border-pink-500/30"
                          : "bg-gradient-to-r from-pink-50 to-red-50 border border-pink-200"
                        : darkMode
                          ? "hover:bg-white/10"
                          : "hover:bg-gray-50"
                        }`}
                    >
                      <div
                        className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 ${activeTab === tab.id
                          ? "bg-gradient-to-r from-pink-600 to-red-500 text-white"
                          : darkMode
                            ? "bg-white/10 text-white/70 group-hover:text-white"
                            : "bg-gray-100 text-gray-600 group-hover:text-gray-900"
                          }`}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-semibold truncate ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          {tab.label}
                        </div>
                        <div
                          className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}
                        >
                          {tab.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Save Changes Button */}
            <div className="mt-4">
              <div
                className={`rounded-2xl shadow-2xl border p-4 ${darkMode
                  ? "bg-[#2c2c3a] border-white/10"
                  : "bg-white border-gray-200"
                  }`}
              >
                <div className="space-y-3">
                  {saveMessage && (
                    <div className="text-xs px-3 py-2 rounded-lg text-center bg-gradient-to-r from-pink-600 to-red-500 text-white">
                      {saveMessage}
                    </div>
                  )}
                  <button
                    onClick={updateUserProfile}
                    disabled={!hasUnsavedChanges || isSaving}
                    className={`w-full px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${hasUnsavedChanges && !isSaving
                      ? "bg-gradient-to-r from-pink-600 to-red-500 text-white hover:scale-105 shadow-lg"
                      : `${darkMode
                        ? "bg-white/10 text-white/40"
                        : "bg-gray-200 text-gray-400"
                      } cursor-not-allowed`
                      }`}
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            <div className="space-y-8 max-w-7xl">
              {activeTab === "profile" && (
                <>
                  <div className="w-full">
                    {/* Profile Information */}
                    <div
                      className={`rounded-2xl shadow-2xl border p-8 ${darkMode
                        ? "bg-[#2c2c3a] border-white/10"
                        : "bg-white border-gray-200"
                        }`}
                    >
                      <div className="mb-6">
                        <h3
                          className={`text-xl font-semibold ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Profile Information
                        </h3>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Avatar Section */}
                        <div
                          className={`flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-dashed transition-all duration-200 hover:border-pink-500/50 ${darkMode
                            ? "border-white/10 bg-white/5"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        >
                          <div className="relative">
                            <img
                              src={preview}
                              alt="avatar"
                              className={`w-28 h-28 rounded-full border-4 object-cover shadow-lg ${darkMode ? "border-white/10" : "border-gray-200"
                                }`}
                            />

                            {/* Hidden File Input */}
                            <input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              className="hidden"
                            />

                            {/* Camera Button */}
                            <button
                              onClick={handleImageClick}
                              className="absolute bottom-1 right-1 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-full p-2 shadow-lg hover:scale-110 transition-all duration-200"
                            >
                              <Camera size={16} />
                            </button>
                          </div>

                          <div className="text-center space-y-1">
                            <button
                              onClick={() => { handleRemovePhoto() }}
                              className={`text-xs font-medium transition-colors duration-200 ${darkMode
                                ? "text-red-400 hover:text-red-300"
                                : "text-red-600 hover:text-red-500"
                                }`}
                            >
                              Remove Photo
                            </button>
                            <p
                              className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"
                                }`}
                            >
                              JPG, PNG or GIF. Max 2MB.
                            </p>
                          </div>
                        </div>

                        {/* Profile Fields */}
                        <div className="lg:col-span-2 space-y-3">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                First Name
                              </label>
                              <input
                                type="text"
                                className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white"
                                  : "bg-gray-50 border-gray-200 text-gray-900"
                                  } border focus:outline-none`}
                                value={firstName}
                                disabled
                              />
                            </div>
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Last Name
                              </label>
                              <input
                                type="text"
                                className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white"
                                  : "bg-gray-50 border-gray-200 text-gray-900"
                                  } border focus:outline-none`}
                                value={lastName}
                                disabled
                              />
                            </div>
                          </div>

                          <div>
                            <label
                              className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                }`}
                            >
                              Email
                            </label>
                            <input
                              type="email"
                              className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                ? "bg-white/10 border-white/10 text-white"
                                : "bg-gray-50 border-gray-200 text-gray-900"
                                } border focus:outline-none`}
                              value={user?.email || ''}
                              disabled
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Job Title
                              </label>
                              <input
                                type="text"
                                className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white"
                                  : "bg-gray-50 border-gray-200 text-gray-900"
                                  } border focus:outline-none`}
                                value={jobTitle}
                                disabled
                              />
                            </div>
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Department
                              </label>
                              <input
                                type="text"
                                className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white"
                                  : "bg-gray-50 border-gray-200 text-gray-900"
                                  } border focus:outline-none`}
                                value={department}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bio Field */}
                      <div className="mt-6">
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Bio
                        </label>
                        <textarea
                          className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                            ? "bg-white/10 border-white/10 text-white placeholder-white/40"
                            : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                            } border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none`}
                          rows={3}
                          value={bio}
                          onChange={(e) => {
                            setBio(e.target.value);
                            markAsChanged();
                          }}
                          placeholder="Tell us about yourself..."
                          maxLength={500}
                        />
                        <div
                          className={`text-xs mt-1 text-right ${darkMode ? "text-white/50" : "text-gray-500"
                            }`}
                        >
                          {bio.length}/500 characters
                        </div>
                      </div>

                      {/* Skills and Expertise Field */}
                      <div className="mt-4">
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Skills and Expertise
                        </label>

                        {/* Add New Skill Input */}
                        <div className="flex sm:flex-row flex-col gap-2 mb-3">
                          <input
                            type="text"
                            className={`flex-1 rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                              ? "bg-white/10 border-white/10 text-white placeholder-white/40"
                              : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                              } border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                            placeholder="Add a skill..."
                            value={newSkill}
                            onChange={(e) => setNewSkill(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={skills.length >= 8}
                          />
                          <button
                            onClick={handleAddSkill}
                            disabled={!newSkill.trim() || skills.length >= 8}
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${newSkill.trim() && skills.length < 8
                              ? "bg-gradient-to-r from-pink-600 to-red-500 text-white hover:scale-105 shadow-lg"
                              : `${darkMode
                                ? "bg-white/10 text-white/40"
                                : "bg-gray-200 text-gray-400"
                              } cursor-not-allowed`
                              }`}
                          >
                            Add
                          </button>
                        </div>

                        <p
                          className={`text-xs mb-3 ${darkMode ? "text-white/50" : "text-gray-500"
                            }`}
                        >
                          These skills will be displayed on your profile in the
                          community page. Maximum 8 skills allowed.
                        </p>

                        {/* Skills Display Section */}
                        <div
                          className={`rounded-lg border ${darkMode
                            ? "bg-white/5 border-white/10"
                            : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="p-4">
                            <h4
                              className={`text-xs font-medium mb-3 ${darkMode ? "text-white/80" : "text-gray-700"
                                }`}
                            >
                              Your Skills ({skills.length}/8)
                            </h4>
                            {skills.length === 0 ? (
                              <p
                                className={`text-xs ${darkMode ? "text-white/50" : "text-gray-500"
                                  }`}
                              >
                                No skills added yet. Add your first skill above.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                {skills.map((skill, index) => (
                                  <div
                                    key={index}
                                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${darkMode
                                      ? "bg-white/5 border border-white/10 hover:bg-white/10"
                                      : "bg-white border border-gray-200 hover:bg-gray-50"
                                      }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`w-2 h-2 rounded-full bg-gradient-to-r from-pink-600 to-red-500`}
                                      ></div>
                                      <span
                                        className={`text-xs font-medium ${darkMode
                                          ? "text-white"
                                          : "text-gray-900"
                                          }`}
                                      >
                                        {skill}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => handleRemoveSkill(skill)}
                                      className={`p-1.5 rounded-lg transition-all duration-200 ${darkMode
                                        ? "hover:bg-red-500/20 text-white/60 hover:text-red-400"
                                        : "hover:bg-red-50 text-gray-400 hover:text-red-600"
                                        }`}
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </>
              )}

              {activeTab === "security" && (
                <div className="w-full">
                  {/* Security Information */}
                  <div
                    className={`rounded-2xl shadow-2xl border p-8 ${darkMode
                      ? "bg-[#2c2c3a] border-white/10"
                      : "bg-white border-gray-200"
                      }`}
                  >
                    <div className="mb-6">
                      <h3
                        className={`text-xl font-semibold ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Security Information
                      </h3>
                    </div>

                    <div className="space-y-6">
                      {/* Password Section */}
                      <div>
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Password
                        </label>
                        <div
                          className={`p-4 rounded-lg border ${darkMode
                            ? "bg-white/5 border-white/10"
                            : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p
                                className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                  }`}
                              >
                                Last changed: {user?.password_changed_at ? new Date(user.password_changed_at).toLocaleDateString() : 'Never'}
                              </p>
                            </div>
                            <button
                              onClick={() => setShowPasswordModal(true)}
                              className="px-4 py-2 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg hover:scale-105 transition-all duration-200 shadow-lg text-xs font-medium"
                            >
                              Change Password
                            </button>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className={`block mb-1 text-xs font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'
                          }`}>
                          Two-Factor Authentication
                        </label>
                        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                          }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-xs ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>
                                Add an extra layer of security to your account
                              </p>
                            </div>
                            <ToggleSwitch
                              label=""
                              checked={twoFactor}
                              onChange={handleTwoFactorToggle}
                              icon={<Shield size={18} />}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "preferences" && (
                <div className="w-full">
                  {/* Preferences Information */}
                  <div
                    className={`rounded-2xl shadow-2xl border p-8 ${darkMode
                      ? "bg-[#2c2c3a] border-white/10"
                      : "bg-white border-gray-200"
                      }`}
                  >
                    <div className="mb-6">
                      <h3
                        className={`text-xl font-semibold ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Preferences Information
                      </h3>
                    </div>

                    <div className="space-y-6">
                      {/* General Section */}
                      <div>
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          General
                        </label>
                        <div
                          className={`p-4 rounded-lg border ${darkMode
                            ? "bg-white/5 border-white/10"
                            : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="space-y-4">
                            {/* Theme */}
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                Theme
                              </label>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p
                                    className={`text-xs ${darkMode
                                      ? "text-white/60"
                                      : "text-gray-600"
                                      }`}
                                  >
                                    Switch between light and dark themes
                                  </p>
                                </div>
                                <ToggleSwitch
                                  label=""
                                  checked={darkMode}
                                  onChange={toggleDarkMode}
                                  icon={<Moon size={18} />}
                                />
                              </div>
                            </div>

                            {/* Language */}
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Language
                              </label>
                              <select
                                className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white focus:ring-pink-500 focus:border-pink-500"
                                  : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-pink-500 focus:border-pink-500"
                                  } border focus:outline-none focus:ring-2`}
                                value={language}
                                onChange={(e) => { setLanguage(e.target.value); markAsChanged(); }}
                              >
                                <option value="en">English</option>
                                {/* <option value="es">Spanish</option>
                                <option value="fr">French</option>
                                <option value="de">German</option>
                                <option value="zh">Chinese</option> */}
                              </select>
                            </div>

                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Time Zone
                              </label>

                              <Dropdown
                                className="
      w-full
      [&>div]:!bg-[#2c2c3a]/90
      [&>div]:!border-white/10
      [&>div]:!min-w-full 
      [&>div]:!w-full 
      max-md:[&>div]:!left-0
      max-md:[&>div]:!right-0
      max-md:[&>div]:!translate-x-0
    "
                                darkMode={darkMode}
                                trigger={
                                  <div
                                    className={`w-full rounded-lg px-3 py-2 transition-all duration-200 cursor-pointer text-sm flex justify-between items-center ${darkMode
                                      ? "bg-white/10 border-white/10 text-white hover:bg-white/20"
                                      : "bg-gray-50 border-gray-200 text-gray-900 hover:bg-white"
                                      } border`}
                                  >
                                    <span>
                                      {
                                        timeZones.find((tz) => tz.value === timeZone)?.label ||
                                        "Select Time Zone"
                                      }
                                    </span>
                                    <ChevronRight size={16} className="text-gray-400 rotate-90" />
                                  </div>
                                }
                              >
                                <div className="p-1 max-h-[300px] overflow-y-auto w-full min-w-full">
                                  {timeZones.map((tz) => (
                                    <button
                                      key={tz.value}
                                      onClick={() => {
                                        setTimeZone(tz.value);
                                        markAsChanged();
                                      }}
                                      className={`block w-full text-left px-2 py-1 text-sm transition-colors duration-150 rounded-xl ${tz.value === timeZone
                                        ? "bg-blue-500 text-white font-semibold"
                                        : darkMode
                                          ? "hover:bg-white/5 text-white"
                                          : "hover:bg-gray-100 text-gray-900"
                                        }`}
                                    >
                                      {tz.label}
                                    </button>
                                  ))}
                                </div>
                              </Dropdown>
                            </div>

                            {/* Date Format */}
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Date Format
                              </label>
                              <select
                                className={`w-full rounded-lg px-3 py-2 transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white focus:ring-pink-500 focus:border-pink-500"
                                  : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-pink-500 focus:border-pink-500"
                                  } border focus:outline-none focus:ring-2`}
                                value={dateFormat}
                                onChange={(e) => { setDateFormat(e.target.value); markAsChanged(); }}
                              >
                                <option>MM/DD/YYYY</option>
                                <option>DD/MM/YYYY</option>
                                <option>YYYY-MM-DD</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Notifications Section */}
                      <div>
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Notifications
                        </label>
                        <div
                          className={`p-4 rounded-lg border ${darkMode
                            ? "bg-white/5 border-white/10"
                            : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="space-y-4">
                            {/* Meeting Reminders */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p
                                    className={`text-xs font-medium ${darkMode ? "text-white" : "text-gray-900"
                                      }`}
                                  >
                                    Meeting Reminders
                                  </p>
                                  <p
                                    className={`text-xs ${darkMode
                                      ? "text-white/60"
                                      : "text-gray-600"
                                      }`}
                                  >
                                    Get notified before meetings start
                                  </p>
                                </div>
                                <ToggleSwitch
                                  label=""
                                  checked={meetingReminders}
                                  onChange={(value) => {
                                    setMeetingReminders(value);
                                    markAsChanged();
                                  }}
                                  icon={<Bell size={18} />}
                                />
                              </div>
                              {meetingReminders && (
                                <div className="flex items-center gap-3">
                                  <label
                                    className={`text-xs ${darkMode
                                      ? "text-white/70"
                                      : "text-gray-600"
                                      }`}
                                  >
                                    Time before start:
                                  </label>
                                  <select
                                    className={`rounded-lg px-2 py-1 text-xs transition-all duration-200 w-24 ${darkMode
                                      ? "bg-white/10 border-white/10 text-white focus:ring-pink-500 focus:border-pink-500"
                                      : "bg-gray-50 border-gray-200 text-gray-900 focus:ring-pink-500 focus:border-pink-500"
                                      } border focus:outline-none focus:ring-2`}
                                    value={reminderMinutes}
                                    onChange={(e) => { setReminderMinutes(e.target.value); markAsChanged() }
                                    }
                                  >
                                    <option value="00:05:00">5 min</option>
                                    <option value="00:10:00">10 min</option>
                                    <option value="00:15:00">15 min</option>
                                    <option value="00:30:00">30 min</option>
                                    <option value="01:00:00">1 hour</option>
                                  </select>
                                </div>
                              )}
                            </div>

                            {/* Meeting Start */}
                            <div className="flex items-center justify-between">
                              <div>
                                <p
                                  className={`text-xs font-medium ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                >
                                  Meeting Start
                                </p>
                                <p
                                  className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                                    }`}
                                >
                                  Get notified when meetings begin
                                </p>
                              </div>
                              <ToggleSwitch
                                label=""
                                checked={meetingStartNotifications}
                                onChange={(value) => {
                                  setMeetingStartNotifications(value);
                                  markAsChanged();
                                }}
                                icon={<Bell size={18} />}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "integrations" && (
                <>
                  <div className="w-full">
                    <div className={`rounded-2xl shadow-2xl border p-8 ${darkMode ? "bg-[#2c2c3a] border-white/10" : "bg-white border-gray-200"}`}>
                      <div className="mb-6">
                        <h3 className={`text-xl font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
                          Calendar Integrations
                        </h3>
                      </div>

                      {calendarError && (
                        <div className={`p-4 border rounded-xl mb-6 ${darkMode ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"}`}>
                          <p className={`text-sm ${darkMode ? "text-red-300" : "text-red-800"}`}>{calendarError}</p>
                        </div>
                      )}

                      <div className="space-y-6">
                        {/* Google Calendar */}
                        <div className={`p-4 rounded-lg border ${darkMode ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-gradient-to-r from-pink-600 to-red-500 flex-shrink-0">
                                <CalendarIcon className="text-white" size={24} />
                              </div>
                              <div>
                                <p className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>Google Calendar</p>
                                <p className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"}`}>
                                  {user?.google_calendar_enabled ? "Connected - Your meetings will sync with Google Calendar" : "Connect to sync your meetings with Google Calendar"}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={user?.google_calendar_enabled ? handleDisconnectGoogle : handleConnectGoogle}
                              disabled={isConnectingGoogle}
                              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${isConnectingGoogle ? "opacity-50 cursor-not-allowed" : ""} ${user?.google_calendar_enabled
                                ? "bg-red-100 text-red-600 hover:bg-red-200"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                                } w-full sm:w-auto`}
                            >
                              {isConnectingGoogle ? "Processing..." : (user?.google_calendar_enabled ? "Disconnect" : "Connect")}
                            </button>
                          </div>
                        </div>

                        {/* Outlook Calendar */}
                        <div className={`p-4 rounded-lg border ${darkMode ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-gradient-to-r from-pink-600 to-red-500 flex-shrink-0">
                                <Mail className="text-white" size={20} />
                              </div>
                              <div>
                                <p className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>Outlook Calendar</p>
                                <p className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"}`}>
                                  {user?.outlook_calendar_enabled ? "Connected - Your meetings will sync with Outlook Calendar" : "Connect to sync your meetings with Outlook Calendar"}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={user?.outlook_calendar_enabled ? handleDisconnectOutlook : handleConnectOutlook}
                              disabled={isConnectingOutlook}
                              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${isConnectingOutlook ? "opacity-50 cursor-not-allowed" : ""} ${user?.outlook_calendar_enabled
                                ? "bg-red-100 text-red-600 hover:bg-red-200"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                                } w-full sm:w-auto`}
                            >
                              {isConnectingOutlook ? "Processing..." : (user?.outlook_calendar_enabled ? "Disconnect" : "Connect")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "devices" && (
                <div className="w-full">
                  {/* Devices Information */}
                  <div
                    className={`rounded-2xl shadow-2xl border p-8 ${darkMode ? "bg-[#2c2c3a] border-white/10" : "bg-white border-gray-200"
                      }`}
                  >
                    <div className="mb-6">
                      <h3
                        className={`text-xl font-semibold ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Devices Information
                      </h3>
                    </div>

                    <div className="space-y-6">
                      {/* Camera Section */}
                      <div>
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Camera
                        </label>
                        <div
                          className={`p-4 rounded-lg border ${darkMode ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="space-y-4">
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Select Device
                              </label>
                              <select
                                className={`w-full rounded-lg px-3 py-2 text-xs transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white focus:ring-pink-500 focus:border-pink-500"
                                  : "bg-white border-gray-200 text-gray-900 focus:ring-pink-500 focus:border-pink-500"
                                  } border focus:outline-none focus:ring-2`}
                                value={selectedCamera}
                                onChange={(e) => setSelectedCamera(e.target.value)}
                              >
                                {deviceInfo.cameras.length > 0 ? (
                                  deviceInfo.cameras.map((c) => (
                                    <option key={c.id} value={c.label}>
                                      {c.label}
                                    </option>
                                  ))
                                ) : (
                                  <>
                                    <option>HD WebCam C270</option>
                                    <option>Built-in Camera</option>
                                    <option>Logitech C920</option>
                                    <option>No Camera</option>
                                  </>
                                )}
                              </select>
                              {deviceLoading && (
                                <p
                                  className={`mt-2 text-xs ${darkMode ? 'text-white/40' : 'text-gray-500'
                                    }`}
                                >
                                  Loading devices…
                                </p>
                              )}
                              {deviceError && (
                                <p
                                  className={`mt-2 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'
                                    }`}
                                >
                                  {deviceError}
                                </p>
                              )}
                            </div>

                            {/* Mirror Toggle Section - Updated for mobile stacking and spacing */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex items-center gap-2 mt-3 sm:mt-0">
                                <button
                                  onClick={openCameraTest}
                                  className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center gap-1.5 text-xs font-medium"
                                  aria-label="Test Camera"
                                >
                                  Test Camera
                                </button>
                                {cameraLoading && (
                                  <span
                                    className={`text-xs ${darkMode ? 'text-white/60' : 'text-gray-600'
                                      }`}
                                  >
                                    Initializing camera…
                                  </span>
                                )}
                                {cameraError && (
                                  <span
                                    className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'
                                      }`}
                                  >
                                    {cameraError}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Microphone Section */}
                      <div>
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Microphone
                        </label>
                        <div
                          className={`p-4 rounded-lg border ${darkMode ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="space-y-4">
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Select Device
                              </label>
                              <select
                                className={`w-full rounded-lg px-3 py-2 text-xs transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white focus:ring-pink-500 focus:border-pink-500"
                                  : "bg-white border-gray-200 text-gray-900 focus:ring-pink-500 focus:border-pink-500"
                                  } border focus:outline-none focus:ring-2`}
                                value={selectedMicrophone}
                                onChange={(e) => setSelectedMicrophone(e.target.value)}
                              >
                                {deviceInfo.microphones.length > 0 ? (
                                  deviceInfo.microphones.map((m) => (
                                    <option key={m.id} value={m.label}>
                                      {m.label}
                                    </option>
                                  ))
                                ) : (
                                  <>
                                  </>
                                )}
                              </select>
                            </div>

                            <div>
                              <div className="space-y-2">
                                {/* Filling Bar - Similar to attached image */}
                                <div className="relative h-2 w-full rounded-full overflow-hidden bg-gray-200 dark:bg-white/10">
                                  <div
                                    className="h-full bg-gradient-to-r from-pink-600 to-red-500 transition-all duration-200 rounded-full"
                                    style={{ width: `${micInputLevel}%` }}
                                  />
                                </div>

                                {/* Audio Level Text */}
                                <div className="flex items-center justify-between">
                                  <span
                                    className={`text-xs ${darkMode ? "text-white/60" : "text-gray-500"
                                      }`}
                                  >
                                    Audio level: {micInputLevel}%
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Test Microphone Button */}
                            <div className="flex items-center gap-2 mt-3 sm:mt-0 flex-nowrap">
                              <button
                                onClick={startMicTest}
                                className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center gap-1.5 text-xs font-medium"
                              >
                                Test Microphone
                              </button>

                              {micTestRunning && (
                                <button
                                  onClick={stopMicTest}
                                  className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-xs font-medium"
                                >
                                  Stop Test
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Speaker/Headphones Section */}
                      <div>
                        <label
                          className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          Speaker/Headphones
                        </label>
                        <div
                          className={`p-4 rounded-lg border ${darkMode ? "bg-white/5 border-white/10" : "bg-gray-50 border-gray-200"
                            }`}
                        >
                          <div className="space-y-4">
                            <div>
                              <label
                                className={`block mb-1 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                                  }`}
                              >
                                Select Output
                              </label>
                              <select
                                className={`w-full rounded-lg px-3 py-2 text-xs transition-all duration-200 ${darkMode
                                  ? "bg-white/10 border-white/10 text-white focus:ring-pink-500 focus:border-pink-500"
                                  : "bg-white border-gray-200 text-gray-900 focus:ring-pink-500 focus:border-pink-500"
                                  } border focus:outline-none focus:ring-2`}
                                value={selectedSpeaker}
                                onChange={(e) => setSelectedSpeaker(e.target.value)}
                              >
                                {deviceInfo.speakers.length > 0 ? (
                                  deviceInfo.speakers.map((s) => (
                                    <option key={s.id} value={s.label}>
                                      {s.label}
                                    </option>
                                  ))
                                ) : (
                                  <>
                                    <option>Built-in Speakers</option>
                                    <option>Headphones</option>
                                    <option>External Speakers</option>
                                    <option>Bluetooth Headphones</option>
                                  </>
                                )}
                              </select>
                            </div>
                            <div className="flex items-center gap-2 mt-4 sm:mt-0">
                              <button
                                onClick={handleTestSpeaker}
                                className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center gap-1.5 text-xs font-medium"
                                aria-label="Test Speaker"
                              >
                                <Volume1 size={12} />
                                Test Speaker
                              </button>
                              {speakerTesting && (
                                <span
                                  className={`text-xs ${darkMode ? 'text-white/60' : 'text-gray-600'
                                    }`}
                                >
                                  Playing test tone…
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {cameraModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="camera-test-title" onClick={closeCameraTest}>
            <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl border ${darkMode ? 'bg-[#2c2c3a] border-white/10' : 'bg-white border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 id="camera-test-title" className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Camera Test</h3>
                  <button onClick={closeCameraTest} className={`p-2 rounded-lg transition-all duration-200 ${darkMode ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`} aria-label="Close Camera Test">
                    <X size={20} />
                  </button>
                </div>
                <div className={`rounded-lg overflow-hidden border ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <video ref={videoRef} className="w-full h-64 object-cover" playsInline muted aria-label="Camera preview" />
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={retryCameraTest} className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg text-xs font-medium" aria-label="Retry Camera">Retry</button>
                  <button onClick={closeCameraTest} className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-xs font-medium" aria-label="Close">Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div
              className={`w-full max-w-md mx-4 rounded-2xl shadow-2xl border ${darkMode
                ? "bg-[#2c2c3a] border-white/10"
                : "bg-white border-gray-200"
                }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3
                    className={`text-xl font-semibold ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Change Password
                  </h3>
                  <button
                    onClick={handleClosePasswordModal}
                    className={`p-2 rounded-lg transition-all duration-200 ${darkMode
                      ? "hover:bg-white/10 text-white/60 hover:text-white"
                      : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                      }`}
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Current Password */}
                  <div>
                    <label
                      className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                    >
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        className={`w-full rounded-lg px-4 py-3 transition-all duration-200 ${darkMode
                          ? "bg-white/10 border-white/10 text-white placeholder-white/40"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                          } border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded ${darkMode
                          ? "hover:bg-white/10 text-white/60 hover:text-white"
                          : "hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                          }`}
                      >
                        {showCurrentPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label
                      className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                    >
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        className={`w-full rounded-lg px-4 py-3 transition-all duration-200 ${darkMode
                          ? "bg-white/10 border-white/10 text-white placeholder-white/40"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                          } border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded ${darkMode
                          ? "hover:bg-white/10 text-white/60 hover:text-white"
                          : "hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                          }`}
                      >
                        {showNewPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm New Password */}
                  <div>
                    <label
                      className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                    >
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={`w-full rounded-lg px-4 py-3 transition-all duration-200 ${darkMode
                          ? "bg-white/10 border-white/10 text-white placeholder-white/40"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                          } border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded ${darkMode
                          ? "hover:bg-white/10 text-white/60 hover:text-white"
                          : "hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                          }`}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleClosePasswordModal}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${darkMode
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg font-medium hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    Change Password
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}

        <ProfileModal
          open={showProfile}
          onClose={() => setShowProfile(false)}
          user={{
            name: "Ledy Sintia",
            email: "ledy.sintia@email.com",
            avatar: "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png",
          }}
          onLogout={() => {
            setShowProfile(false); /* add logout logic here */
          }}
        />


        {showTwoFactorModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div
              className={`w-full max-w-md mx-4 rounded-2xl shadow-2xl border ${darkMode
                ? 'bg-[#2c2c3a] border-white/10'
                : 'bg-white border-gray-200'
                }`}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3
                    className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'
                      }`}
                  >
                    Setup Two-Factor Authentication
                  </h3>
                  <button
                    onClick={() => setShowTwoFactorModal(false)}
                    className={`p-2 rounded-lg transition-all duration-200 ${darkMode
                      ? 'hover:bg-white/10 text-white/60 hover:text-white'
                      : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                      }`}
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="space-y-6">
                  {twoFactorSetupStep === 'initial' ? (
                    <>
                      <div className="text-center">
                        <p
                          className={`text-sm mb-4 ${darkMode ? 'text-white/70' : 'text-gray-600'
                            }`}
                        >
                          Scan this QR code with your authenticator app
                        </p>
                        <div className="inline-block p-4 bg-white rounded-xl">
                          <img
                            src={twoFactorQRCode}
                            alt="2FA QR Code"
                            className="w-48 h-48"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => setTwoFactorSetupStep('verify')}
                        className="w-full px-4 py-3 bg-gradient-to-r from-pink-600 to-red-500 text-white rounded-lg font-medium hover:scale-105 transition-all duration-200 shadow-lg"
                      >
                        Next
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Form for Verification Code */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (twoFactorVerificationCode.length === 6) {
                            handleVerifyTwoFactor();
                          }
                        }}
                      >
                        <div>
                          <label
                            className={`block mb-2 text-xs font-medium ${darkMode ? 'text-white/70' : 'text-gray-600'
                              }`}
                          >
                            Enter Verification Code
                          </label>
                          <input
                            type="text"
                            maxLength={6}
                            className={`w-full rounded-lg px-4 py-3 text-center text-2xl tracking-wider transition-all duration-200 ${darkMode
                              ? 'bg-white/10 border-white/10 text-white'
                              : 'bg-gray-50 border-gray-200 text-gray-900'
                              } border focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500`}
                            value={twoFactorVerificationCode}
                            onChange={(e) =>
                              setTwoFactorVerificationCode(
                                e.target.value.replace(/\D/g, '').slice(0, 6)
                              )
                            }
                            placeholder="000000"
                          />
                        </div>

                        {error && (
                          <div
                            className={`p-4 border rounded-xl mt-4 ${darkMode
                              ? 'bg-red-500/10 border-red-500/20'
                              : 'bg-red-50 border-red-200'
                              }`}
                          >
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg
                                  className="h-5 w-5 text-red-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <div className="ml-3 flex-1">
                                <p
                                  className={`text-sm ${darkMode
                                    ? 'text-red-300'
                                    : 'text-red-800'
                                    }`}
                                >
                                  {error}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={twoFactorVerificationCode.length !== 6}
                          className={`w-full mt-4 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${twoFactorVerificationCode.length === 6
                            ? 'bg-gradient-to-r from-pink-600 to-red-500 text-white hover:scale-105 shadow-lg'
                            : `${darkMode
                              ? 'bg-white/10 text-white/40'
                              : 'bg-gray-200 text-gray-400'
                            } cursor-not-allowed`
                            }`}
                        >
                          Verify and Enable
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showDisable2FAModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className={`w-full max-w-md mx-4 rounded-2xl shadow-2xl border ${darkMode ? 'bg-[#2c2c3a] border-white/10' : 'bg-white border-gray-200'
              }`}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                    Disable Two-Factor Authentication
                  </h3>
                  <button
                    onClick={() => setShowDisable2FAModal(false)}
                    className={`p-2 rounded-lg transition-all duration-200 ${darkMode
                      ? 'hover:bg-white/10 text-white/60 hover:text-white'
                      : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                      }`}
                  >
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleDisable2FA}>
                  <div className="space-y-4">
                    <div>
                      <label className={`block mb-2 text-sm font-medium ${darkMode ? 'text-white/70' : 'text-gray-700'
                        }`}>
                        Enter Verification Code
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        value={disableToken}
                        onChange={(e) => setDisableToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className={`w-full px-4 py-3 rounded-lg border ${darkMode
                          ? 'bg-white/5 border-white/10 text-white'
                          : 'bg-white border-gray-300'
                          }`}
                        placeholder="Enter 6-digit code"
                      />
                    </div>


                    {error && (
                      <div className={`p-4 border rounded-xl ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>{error}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={disableToken.length !== 6}
                      className="w-full px-4 py-3 text-white bg-gradient-to-r from-pink-600 to-red-500 rounded-lg hover:scale-105 shadow-lg disabled:opacity-50"
                    >
                      Disable 2FA
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Settings;

